import env from '../resources/env-constants';
import type { Fan } from '../types/idp-types';
import { UserEvents, IdpClientProducts, CompetionsManaged } from '../types/idp-types';
import LocalStorageClient from '../utils/LocalStorageClient';

import { patchUser } from './apis';

const endpoint = env.API_ENDPOINT;
const idpClientId = endpoint.idpClientId;
const idpUrl = endpoint.idp_url;

const getCompetitionName = (competitionId: string): keyof typeof IdpClientProducts | undefined => {
  const key = Object.keys(CompetionsManaged).find(
    (k) => CompetionsManaged[k as keyof typeof CompetionsManaged] === competitionId,
  );

  if (key && key in IdpClientProducts) {
    return IdpClientProducts[key as keyof typeof IdpClientProducts];
  }
  return undefined;
};

const getBasePath = (withTrailingSlash: boolean = true) => {
  if (window.location.hostname === 'localhost') {
    withTrailingSlash = false;
  }
  const basePath = `${window.location.origin}${window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'))}`;
  return withTrailingSlash
    ? `${basePath}${basePath.endsWith('/') ? '' : '/'}`
    : `${basePath.endsWith('/') ? basePath.substring(0, basePath.lastIndexOf('/')) : basePath}`;
};

const handleLogin = (user: Fan) => {
  if (Object.entries(user).length !== 0 && user.sub) {
    const votes = LocalStorageClient.getVote();

    if (votes?.userId) {
      patchUser(votes.userId, user.sub).catch(console.error);
    }
  }
};

const appendComponentScript = () => {
  return new Promise<void>((resolve) => {
    let script = document.querySelector<HTMLScriptElement>(`script[src*="${idpUrl}"]`);

    if (script) return resolve();
    script = document.createElement('script');

    script.src = idpUrl;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
};

export const waitForIdp = async () => {
  while (!window.hasOwnProperty('IdpManager')) await new Promise((resolve) => setTimeout(resolve, 100));
};

export const idpInit = async (locale?: string, cup?: string) => {
  await appendComponentScript();
  await waitForIdp();

  const config = {
    client_id: idpClientId,
    return_uri: getBasePath(),
    locale: locale,
    scope: 'openid address email offline_access idp_profile idp_favourites p1:update:user:apps',
    acquisitionSettings: {
      regProduct: getCompetitionName(cup),
    },
  };

  await window?.IdpManager.initialize(config);

  if (window?.IdpManager.isReady) {
    const eventEmitter = window.IdpManager?.getEventEmitter?.();

    if (eventEmitter) {
      eventEmitter.on(UserEvents.LOGIN, handleLogin);
      eventEmitter.on(UserEvents.FAN_LOADED, handleLogin);
    }
  }

  return window.IdpManager;
};

export const showLogin = async () => {
  await waitForIdp();
  await window?.IdpManager.login();
};

export const showRegister = async () => {
  await waitForIdp();
  await window?.IdpManager.register();
};

export const isUserLogged = async () => await window?.IdpManager.isFanLoggedIn();
export const getCurrentUser = async () => await window?.IdpManager.getFan();
